import { useState } from 'react'

import { api } from '../api/client'
import type { ConfigDiff, ConfigRevision } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { ErrorBanner, Spinner } from './common'

/**
 * Le viste di una revisione di configurazione — il confronto con la precedente,
 * il testo intero con copia e scarica — condivise dalla pagina «Configurazioni
 * apparati» e dalla scheda Backup del CI. Una copia per pagina divergerebbe alla
 * prima correzione.
 */

export function Confronto({ revisione }: { revisione: ConfigRevision }) {
  const diff = useAsync<ConfigDiff>(
    () => api.configDiff(revisione.id),
    [revisione.id],
  )

  if (diff.error) return <ErrorBanner error={diff.error} />
  if (diff.loading) return <Spinner />

  return (
    <>
      {/* Il cambio di trasporto va detto **prima** del confronto: senza, si
          legge «tutto cambiato» e si va a cercare chi ha rifatto lo switch. */}
      {diff.data?.warning && (
        <div className="banner banner--warn">{diff.data.warning}</div>
      )}
      {diff.data?.truncated && (
        <div className="banner banner--info">
          {t("Il confronto è troppo lungo ed è stato tagliato: quello che vedi è l'inizio. La configurazione intera è nell'altra scheda.")}
        </div>
      )}
      {diff.data && !diff.data.from && (
        <div className="banner banner--info">
          {t("È il primo salvataggio di questo apparato: non c'è un «prima», e tutto risulta aggiunto.")}
        </div>
      )}

      <p className="muted">
        {t('+{aggiunte} righe aggiunte, −{tolte} tolte', {
          aggiunte: diff.data?.added ?? 0,
          tolte: diff.data?.removed ?? 0,
        })}
      </p>
      <pre className="diff">
        {(diff.data?.lines ?? []).map((riga, i) => (
          <div key={i} className={classeRiga(riga)}>
            {riga || ' '}
          </div>
        ))}
      </pre>
    </>
  )
}

/**
 * La configurazione per intero.
 *
 * Mancava del tutto: si poteva leggere la differenza e non il testo — cioè
 * sapere che una riga era comparsa e non cosa c'era intorno. Ed è anche la
 * risposta alla domanda per cui un backup esiste: «ridammi com'era».
 */
export function Intera({ revisione, nome }: { revisione: ConfigRevision; nome: string }) {
  const testo = useAsync(() => api.configRevision(revisione.id), [revisione.id])
  const [copiato, setCopiato] = useState(false)

  if (testo.error) return <ErrorBanner error={testo.error} />
  if (testo.loading) return <Spinner />

  const contenuto = testo.data?.content ?? ''

  return (
    <>
      <div className="rowactions" style={{ textAlign: 'left', marginBottom: 8 }}>
        <button
          type="button"
          className="secondary"
          onClick={() => {
            void navigator.clipboard.writeText(contenuto)
            setCopiato(true)
            window.setTimeout(() => setCopiato(false), 2000)
          }}
        >
          {copiato ? t('Copiata') : t('Copia')}
        </button>{' '}
        <button
          type="button"
          className="secondary"
          onClick={() => scarica(contenuto, nome, revisione)}
        >
          {t('Scarica')}
        </button>
      </div>
      <p className="muted" style={{ fontSize: 11 }}>
        {/* Va detto ogni volta che si mostra il testo: chi la usa per
            ripristinare deve sapere che le credenziali non ci sono più. */}
        {t('Le credenziali sono state mascherate prima di conservarla: per ripristinare da qui vanno rimesse a mano.')}
      </p>
      <pre className="diff">{contenuto}</pre>
    </>
  )
}

/** Scarica la configurazione come file di testo. */
export function scarica(contenuto: string, nome: string, revisione: ConfigRevision) {
  const quando = new Date(revisione.collected_at)
    .toISOString()
    .slice(0, 19)
    .replace(/[:T]/g, '-')
  const blob = new Blob([contenuto], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  // Nome, data e trasporto: due file scaricati dallo stesso apparato in due
  // momenti devono distinguersi senza doverli aprire.
  a.download = `${nome.replace(/[^\w.-]+/g, '_')}-${quando}-${revisione.transport}.txt`
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Il colore di una riga del confronto.
 *
 * Colore **e** simbolo: il `+` e il `-` restano, perché chi non distingue il
 * verde dal rosso deve poter leggere lo stesso confronto.
 */
export function classeRiga(riga: string): string {
  if (riga.startsWith('+++') || riga.startsWith('---')) return 'diff__file'
  if (riga.startsWith('@@')) return 'diff__hunk'
  if (riga.startsWith('+')) return 'diff__add'
  if (riga.startsWith('-')) return 'diff__del'
  return ''
}
